/**
 * Générateur — Il utilise les préfixes de nano à giga.
 * Attendu ID : 138 | Niveau : 4ème | Domaine : Nombres (desc 11, O3)
 *
 * Source doc prof (Cluster 1, O3) : relier préfixe ↔ puissance de 10, compléter
 * avec le bon préfixe (clé USB de 32×10⁹ octets → gigaoctets), conversions en
 * puissances de 10, ranger des longueurs. Plan de cartes validé 2026-07-14 :
 *
 *   Carte « relier » — préfixe (symbole) ↔ 10ⁿ (relier)
 *   Carte « menu » — le bon préfixe pour un ordre de grandeur (qcm_unique)
 *   Carte « conversion » — a × 10ᵖ (préfixe₁ unité) = a × 10ⁿ (préfixe₂ unité),
 *                          mantisse inchangée → exposant n (texte à trous, saisie)
 *   Carte « ranger » — ranger des grandeurs (ordonner)
 *
 * NEUF pour le 4e : table PREFIXES (nano→giga) du module nombres.js.
 * Tout par construction ; ordre décidable (exposants distincts → pas d'ex æquo).
 *
 * Revue 2026-09-22 (audit dénombré, décisions prof) :
 *   - « menu » : les octets et les hertz ne prennent plus que kilo, méga, giga
 *     (35 % des tirages donnaient un nano-octet ou un hecto-hertz) ; chaque
 *     contexte a son sujet et son verbe (un fichier pèse, une fréquence vaut) ;
 *     coefficient entier de 2 à 9 OU décimal de 1,1 à 9,9 ; distracteurs = les
 *     deux préfixes d'exposant voisin plus un tiré au hasard (la confusion
 *     réelle est kilo/méga, pas kilo/nano).
 *   - « conversion » RÉÉCRITE : elle demandait la puissance de 10 d'un préfixe
 *     (doublon de « relier » et « menu » sous un troisième format) ; elle
 *     convertit maintenant une grandeur entre deux unités préfixées, mantisse
 *     inchangée, écart d'exposant 3, 6 ou 9 (4,2 × 10⁷ g = 4,2 × 10ⁿ kg → n = 4).
 *     La lettre n passe en KaTeX, la consigne du signe moins est une phrase.
 *
 * Revue 2026-09-23 : tout ce qui s'affiche comme puissance passe en KaTeX
 * (règle de la maison, décision prof) : puissTex et tex de generators/utils.js
 * remplacent puiss de nombres.js (exposants Unicode) dans les cibles de
 * « relier » et l'énoncé de « menu ». Même jour, demande prof : « conversion »
 * n'est plus une saisie nue mais une phrase à trous en mode mathématique
 * global, l'égalité entière affichée (coefficient, puissances et unités dans
 * une seule formule, au lieu d'un mélange KaTeX pour la lettre et Unicode pour
 * les nombres) et le trou à la place de l'exposant cherché, protégé par un
 * groupe (« $4{,}2 \times 10^{7}\ \text{g} = 4{,}2 \times 10^{{{4}}}\ \text{kg}$ »,
 * modèle gen_205) ; clé conversion_texte → conversion_texte_trou_saisie. Les
 * unités passent en \text{} et ne deviennent jamais des puissances ; le signe
 * micro n'a pas de glyphe dans la police texte de KaTeX, il s'écrit \mu. La
 * réponse (exposant saisi, signé en ASCII) ne change pas.
 *
 * Composition 2026-09-23 (règle prof, cartes relier) : « relier » passe de
 * cinq paires à trois préfixes pour cinq puissances, les trois bonnes et deux
 * leurres (cibles_leurres de makeRelier), toujours moins d'éléments à gauche
 * qu'à droite. Les leurres sont des puissances d'autres préfixes de la table :
 * le préfixe voisin (10⁶ pour kilo) et le préfixe miroir d'exposant opposé
 * (10⁻³ pour kilo) ; jamais une bonne cible ni deux fois la même valeur.
 * L'énoncé prévient que des puissances restent orphelines.
 */

import {
    randomChoice, randInt, shuffle, tex, puissTex,
    makeQcm, makeTexteTrou, makeRelier, makeOrdonner,
} from '../../../utils';
import { PREFIXES } from './nombres';

const ATTENDU_ID = 138;

const L_RELIER = 'Associer un préfixe à sa puissance de 10';
const L_MENU = 'Choisir le préfixe adapté à un ordre de grandeur';
const L_CONVERSION = 'Convertir une grandeur entre deux unités à préfixe';
const L_RANGER = 'Ranger des grandeurs avec préfixes';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Préfixes réellement employés pour les octets et les hertz : kilo, méga, giga
// (un nano-octet ou un hecto-hertz n'existe pas).
const GRANDS = PREFIXES.filter(p => p.e >= 3);
// Contextes de la carte « menu » : sujet et verbe propres, préfixes admis.
const CONTEXTES = [
    { unite: 'octets', sujet: 'Un fichier', verbe: 'pèse', prefixes: GRANDS },
    { unite: 'mètres', sujet: 'Une longueur', verbe: 'mesure', prefixes: PREFIXES },
    { unite: 'grammes', sujet: 'Une masse', verbe: 'vaut', prefixes: PREFIXES },
    { unite: 'hertz', sujet: 'Une fréquence', verbe: 'vaut', prefixes: GRANDS },
];
const BASES = [{ sym: 'm', nom: 'mètre' }, { sym: 'g', nom: 'gramme' }, { sym: 'L', nom: 'litre' }];

// Coefficient affiché : entier de 2 à 9, ou décimal de 1,1 à 9,9 (décimale non nulle).
const coefAlea = () => (randomChoice([true, false]) ? String(randInt(2, 9)) : `${randInt(1, 9)},${randInt(1, 9)}`);

// Décimal français en LaTeX : « 4,2 » → « 4{,}2 » (sinon KaTeX espace la virgule).
const nbTex = (s) => String(s).replace(',', '{,}');
// « a × 10ⁿ » en KaTeX.
const sciTex = (coef, e) => tex(`${nbTex(coef)} \\times 10^{${e}}`);
// Symbole d'unité dans une formule (police droite, jamais une puissance).
// Le signe micro « µ » (U+00B5) n'a pas de glyphe dans la police texte de
// KaTeX (« No character metrics », le mu grec non plus) : il s'écrit \mu.
const uniteTex = (sym) => (sym.startsWith('µ') ? `\\mu\\text{${sym.slice(1)}}` : `\\text{${sym}}`);

// Les deux préfixes d'exposant voisin dans la table (aux bords, les deux plus
// proches). Servent aux leurres de « relier » et aux distracteurs de « menu ».
const voisins = (p) => {
    const i = PREFIXES.indexOf(p);
    const idx = i === 0 ? [1, 2] : i === PREFIXES.length - 1 ? [i - 1, i - 2] : [i - 1, i + 1];
    return idx.map(j => PREFIXES[j]);
};

// ═════════ Carte « relier » — préfixe (symbole) ↔ 10ⁿ ═════════

// Composition 2026-09-23 (règle prof) : trois préfixes à gauche, cinq
// puissances à droite, les trois bonnes et deux leurres qui sont les
// puissances d'AUTRES préfixes de la table, jamais du remplissage :
//   · prefixe_voisin : la puissance du préfixe voisin dans la table (10⁶ pour
//     kilo, 10⁻² pour milli), la confusion réelle kilo / méga, milli / centi,
//     celle que la carte « menu » vise déjà ;
//   · signe_oppose : la puissance de l'exposant opposé (10⁻³ pour kilo) ; la
//     table est symétrique, c'est le préfixe miroir (kilo / milli, méga /
//     micro, giga / nano), le signe de l'exposant oublié.
// Un leurre n'est jamais une bonne cible ni un autre leurre : filtre sur la
// chaîne affichée (puissTex), injective en l'exposant. Sept préfixes restent
// hors sélection et les voisins libres d'une sélection de trois sont au moins
// deux : la carte a toujours ses deux leurres, sans boucle.
const genRelier = () => {
    const sel = shuffle(PREFIXES).slice(0, 3);
    const pairs = sel.map(p => ({ source: `${p.nom} (${p.sym})`, target: puissTex(10, p.e) }));
    const pris = new Set(pairs.map(p => p.target));
    const leurres = [];
    const ajouter = (e, code) => {
        const target = puissTex(10, e);
        if (leurres.length >= 2 || pris.has(target)) return;
        pris.add(target);
        leurres.push({ target, code });
    };
    const candVoisins = shuffle(sel.flatMap(voisins)).map(q => q.e);
    const miroirs = shuffle(sel.map(p => -p.e));
    const voisin = candVoisins.find(e => !pris.has(puissTex(10, e)));
    if (voisin !== undefined) ajouter(voisin, 'prefixe_voisin');
    miroirs.forEach(e => ajouter(e, 'signe_oppose'));
    candVoisins.forEach(e => ajouter(e, 'prefixe_voisin'));
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque préfixe à la puissance de 10 qui lui correspond.</b> Attention : certaines puissances de 10 ne correspondent à aucun préfixe de la liste.</p>',
        { ...opts(L_RELIER), cibles_leurres: leurres });
    payload._meta.params = {
        prefixes: sel.map(p => p.nom),
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ═════════ Carte « menu » — le bon préfixe pour un ordre de grandeur ═════════

const genMenuQcm = () => {
    const ctx = randomChoice(CONTEXTES);
    const p = randomChoice(ctx.prefixes);
    const coef = coefAlea();
    const proches = voisins(p);
    const autre = randomChoice(PREFIXES.filter(q => q.e !== p.e && !proches.includes(q)));
    const choix = [p, ...proches, autre].map(q => q.nom);
    const texte = `<p>${ctx.sujet} ${ctx.verbe} ${sciTex(coef, p.e)} ${ctx.unite}.</p>`
        + `<p><b>On peut aussi dire ${coef} … ${ctx.unite}. Quel préfixe faut-il utiliser ?</b></p>`;
    const payload = makeQcm(choix, p.nom, texte, opts(L_MENU));
    payload._meta.params = { prefixe: p.nom, e: p.e, contexte: ctx.unite, coef };
    return payload;
};

// ═════════ Carte « conversion » — a × 10ᵖ (unité 1) = a × 10ⁿ (unité 2) ═════════

// Préfixes « de trois en trois » : nano, micro, milli, l'unité nue, kilo, méga, giga.
const UNITE_NUE = { nom: '', sym: '', e: 0 };
const PAS_DE_TROIS = [...PREFIXES.filter(p => p.e % 3 === 0), UNITE_NUE].sort((a, b) => a.e - b.e);
const KILO = PREFIXES.find(p => p.nom === 'kilo');

const genConversionTrous = () => {
    const base = randomChoice(BASES);
    const coef = coefAlea();
    // Deux préfixes distincts d'écart 3, 6 ou 9 ; l'exposant de départ et
    // l'exposant cherché restent lisibles (|n| ≤ 12). Boucle bornée, repli sur
    // l'exemple du prof : 4,2 × 10⁷ g = 4,2 × 10⁴ kg.
    let p1 = UNITE_NUE; let p2 = KILO; let e1 = 7; let n = 4;
    for (let i = 0; i < 60; i++) {
        const [q1, q2] = shuffle(PAS_DE_TROIS).slice(0, 2);
        const f = randInt(-6, 9);
        const m = f + q1.e - q2.e;
        if (Math.abs(q1.e - q2.e) <= 9 && Math.abs(m) <= 12) {
            p1 = q1; p2 = q2; e1 = f; n = m;
            break;
        }
    }
    const u1 = uniteTex(`${p1.sym}${base.sym}`);
    const u2 = uniteTex(`${p2.sym}${base.sym}`);
    // L'égalité entière en mode mathématique (dollars aux deux bouts, cf.
    // hasMathLines), le trou à la place de l'exposant cherché, protégé par un
    // groupe : 10^{{{n}}} et jamais 10^{{n}} (KaTeX refuse un ^ suivi du markup
    // du trou, cf. gen_205). Ligne courte, au plus « 4,2 × 10⁻⁶ daL = 4,2 × 10^⬚ µL ».
    const phrase = `$${nbTex(coef)} \\times 10^{${e1}}\\ ${u1} = ${nbTex(coef)} \\times 10^{{{${n}}}}\\ ${u2}$`;
    const texte = `<p>On exprime une même grandeur avec deux unités différentes. Le nombre ${coef} ne change pas, `
        + 'seule la puissance de 10 change.</p>'
        + '<p><b>Complète l\'exposant.</b></p>'
        + '<p>Si l\'exposant est négatif, écris-le avec le signe moins, comme -3.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CONVERSION), mode: 'saisie' });
    payload._meta.params = { coef, base: base.sym, de: p1.nom || 'unité', vers: p2.nom || 'unité', e1, n };
    return payload;
};

// ═════════ Carte « ranger » — ranger des grandeurs (ordonner) ═════════

const genRangerOrdonner = () => {
    const k = randomChoice([4, 5]);
    const sel = shuffle(PREFIXES).slice(0, k);
    const base = randomChoice(BASES);
    // Coefficients DISTINCTS : le check A-ORDONNER-EQ ne lit que le coefficient
    // de tête (parseNumFr ignore le préfixe) → deux items de même coefficient
    // seraient vus « égaux ». L'ordre vrai reste par la valeur coef × 10^e.
    const coefs = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, k);
    const items = sel.map((p, i) => ({ txt: `${coefs[i]} ${p.sym}${base.sym}`, val: coefs[i] * 10 ** p.e }));
    const croissant = randomChoice([true, false]);
    const ordre = [...items].sort((a, b) => croissant ? a.val - b.val : b.val - a.val).map(o => o.txt);
    const sens = croissant ? 'de la plus petite à la plus grande' : 'de la plus grande à la plus petite';
    const payload = makeOrdonner(ordre,
        `<p><b>Range ces grandeurs ${sens}.</b></p>`, opts(L_RANGER));
    payload._meta.params = { croissant };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    relier_relier: genRelier,
    menu_qcm_unique: genMenuQcm,
    conversion_texte_trou_saisie: genConversionTrous,
    ranger_ordonner: genRangerOrdonner,
};

const FORMAT_LABELS = {
    relier: 'Relier', qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', ordonner: 'Ranger',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'relier', label: L_RELIER, formatsList: buildFormats('relier') },
    { id: 'menu', label: L_MENU, formatsList: buildFormats('menu') },
    { id: 'conversion', label: L_CONVERSION, formatsList: buildFormats('conversion') },
    { id: 'ranger', label: L_RANGER, formatsList: buildFormats('ranger') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
